import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { FilePenLine } from "lucide-react";
import { listAllGiftCardsManager } from "../../reducers/giftcard.slice";
import { AuthContext } from "../../context/authContext";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import PageHeader from "../../components/shared/PageHeader";
import SearchBar from "../../components/shared/SearchBar";
import RefreshButton from "../../components/shared/RefreshButton";
import useMediaQuery from "../../components/shared/useMediaQuery";
import GiftCardTile, { giftCardTypeLabel, retailerTitle } from "../../components/member/giftcards/GiftCardTile";
import AddGiftCardFormPage from "./AddGiftCardPage";
import EditGiftCardFormPage from "./EditGiftCardPage";
import { companyConfig } from "./myGiftCardList";
import dayjs from "dayjs";

const statStyle = (accent) => ({
  background: "linear-gradient(180deg, rgba(255,255,255,0.025), transparent)",
  border: "1px solid rgba(255,255,255,0.06)",
  borderTop: `3px solid ${accent}`,
  borderRadius: 14,
  padding: "16px 18px",
});

const statLabelStyle = {
  display: "block",
  color: "#c3c8cc",
  fontSize: 10.5,
  fontWeight: 800,
  letterSpacing: "0.09em",
};

const statValueStyle = {
  display: "block",
  color: "#fff",
  fontFamily: "ui-monospace, SF Mono, Menlo, monospace",
  fontSize: 24,
  fontWeight: 800,
  marginTop: 8,
};

const GiftCardManagerPage = () => {
  const [storeCardData, setStoreCardData] = useState([]);
  const [showLoader, setShowLoader] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  const dispatch = useDispatch();
  const { showToast } = useContext(AuthContext);
  const isMobile = useMediaQuery("(max-width: 640px)");

  const getAllCards = () => {
    setShowLoader(true);
    dispatch(listAllGiftCardsManager()).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        showToast(res?.error?.message, "error");
        setShowLoader(false);
        return;
      }
      const formatted = res?.payload?.map((item) => ({
        ...item,
        created_at: dayjs(item?.created_at).format("MM/DD/YYYY"),
      }));
      setStoreCardData(formatted);
      setShowLoader(false);
    });
  };

  useEffect(() => { getAllCards(); }, []);

  const filteredData = searchTerm.trim()
    ? storeCardData.filter((card) => card?.retailer?.toLowerCase().includes(searchTerm.toLowerCase()))
    : storeCardData;

  const giftCardStats = useMemo(() => ({
    total: storeCardData.length,
    active: storeCardData.filter((d) => d.status === "ACTIVE").length,
    inactive: storeCardData.filter((d) => d.status !== "ACTIVE").length,
  }), [storeCardData]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: isMobile ? 24 : 32, width: "100%" }}>
      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          gap: 12,
        }}
      >
        <PageHeader
          title="Gift Card Inventory"
          subtitle="Manage gift card inventory to track available cards, denominations, and retailers for your customers."
        />
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", width: isMobile ? "100%" : "auto", flexShrink: 0 }}>
          <RefreshButton onRefresh={getAllCards} spinning={showLoader} />
          <SearchBar placeholder="Search by retailer…" searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
          <Button onClick={() => setCreateOpen(true)} style={{ whiteSpace: "nowrap" }}>
            Add New Gift Card
          </Button>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? "130px" : "160px"}, 1fr))`,
          gap: isMobile ? 10 : 14,
        }}
      >
        <div style={statStyle("#00ADB5")}>
          <span style={statLabelStyle}>TOTAL</span>
          <span style={statValueStyle}>{giftCardStats.total}</span>
        </div>
        <div style={statStyle("#4caf50")}>
          <span style={statLabelStyle}>ACTIVE</span>
          <span style={statValueStyle}>{giftCardStats.active}</span>
        </div>
        <div style={statStyle("#f27059")}>
          <span style={statLabelStyle}>INACTIVE</span>
          <span style={statValueStyle}>{giftCardStats.inactive}</span>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? 180 : 210}px, 1fr))`,
          gap: isMobile ? 12 : 16,
        }}
      >
        {filteredData?.map((card, index) => {
          const { logo, bg } = companyConfig[card?.retailer] || { logo: "/images/amazon.png", bg: "#000000" };
          return (
            <GiftCardTile
              key={index}
              logo={logo}
              bg={bg}
              retailerName={retailerTitle(card?.retailer)}
              typeText={giftCardTypeLabel(card?.gift_card_type)}
              priceText={`$${card?.amount}`}
              secondaryText={`${card?.boost}% Boost • Added ${card?.created_at}`}
              pillText={card?.status}
              pillTone={card?.status === "ACTIVE" ? "success" : "danger"}
              action={
                <button
                  type="button"
                  aria-label={`Edit ${retailerTitle(card?.retailer)} gift card`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditData(card);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 6,
                    border: "1px solid rgba(255,255,255,0.18)",
                    borderRadius: 8,
                    color: "#fff",
                    background: "rgba(10,12,14,0.55)",
                    cursor: "pointer",
                  }}
                >
                  <FilePenLine size={15} />
                </button>
              }
            />
          );
        })}
      </div>
      <Dialog open={createOpen} onOpenChange={(open) => !open && setCreateOpen(false)}>
        <DialogContent style={{ maxWidth: 560, width: isMobile ? "calc(100vw - 16px)" : "90vw", maxHeight: "92vh" }}>
          <DialogHeader>
            <DialogTitle>Add Gift Card</DialogTitle>
            <DialogDescription>Create a new gift card listing for your inventory.</DialogDescription>
          </DialogHeader>
          {createOpen && (
            <AddGiftCardFormPage
              dialogMode
              onCancel={() => setCreateOpen(false)}
              onSuccess={() => {
                setCreateOpen(false);
                getAllCards();
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(editData)} onOpenChange={(open) => !open && setEditData(null)}>
        <DialogContent style={{ maxWidth: 560, width: isMobile ? "calc(100vw - 16px)" : "90vw", maxHeight: "92vh" }}>
          <DialogHeader>
            <DialogTitle>Edit Gift Card</DialogTitle>
            <DialogDescription>Update this gift card listing.</DialogDescription>
          </DialogHeader>
          {editData && (
            <EditGiftCardFormPage
              dialogMode
              cardDetails={editData}
              onCancel={() => setEditData(null)}
              onSuccess={() => {
                setEditData(null);
                getAllCards();
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GiftCardManagerPage;
